import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Info } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SyncLogDetailDialog } from '@/components/sync-logs';
import type { RemovalSkipReason, SyncLog } from '@/types/api';

interface SyncLogsListProps {
  logs: SyncLog[];
  /** When removal detection last ran for the account (derived from its sync logs). */
  lastRemovalCheckAt: string | null;
  isLoading?: boolean;
}

/**
 * Removal detection soft-deletes listings a sync did not see, so it runs only
 * on a fetch shown complete. These say why a sync skipped it.
 */
const REMOVAL_SKIP_LABELS: Record<RemovalSkipReason, string> = {
  sync_limit: 'Sync listing cap',
  paused: 'Sync paused',
  count_unknown: 'Partner count unknown',
  count_short: 'Fewer listings than the partner reports',
  duplicates: 'Listings served twice',
};

/** The sync job's prefix for its removal-skip note in errorDetails. */
const REMOVAL_NOTE_PREFIX = 'Removal detection skipped:';

/**
 * The removal note alone. errorDetails joins several notes (propagation
 * failures first) and the sync job writes the removal note last, so it runs
 * from its prefix to the end.
 */
function removalNoteOf(errorDetails: string | null): string | null {
  if (errorDetails === null) return null;
  const start = errorDetails.indexOf(REMOVAL_NOTE_PREFIX);
  return start === -1 ? null : errorDetails.slice(start).trim();
}

function RemovalCheckCell({ log }: { log: SyncLog }): JSX.Element {
  if (log.removalCheck === 'ran') {
    return (
      <span className="flex items-center gap-1">
        <Badge variant="outline">Checked</Badge>
        {log.locationsDeleted > 0 && (
          <span className="text-xs text-muted-foreground">{log.locationsDeleted} removed</span>
        )}
      </span>
    );
  }
  if (log.removalCheck === 'skipped') {
    const label = log.removalSkipReason ? REMOVAL_SKIP_LABELS[log.removalSkipReason] : 'Reason not recorded';
    const detail = log.removalSkipReason === 'duplicates' ? `${label} (${String(log.duplicatesSkipped)})` : label;
    const note = removalNoteOf(log.errorDetails);
    return (
      <span className="flex flex-col gap-0.5">
        <Badge variant="warning">Skipped</Badge>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          {detail}
          {note !== null && (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* 14 px icon + 5 px padding each side = a 24x24 hit area; the
                    matching negative margin keeps the row layout unchanged. */}
                <button
                  type="button"
                  aria-label="Why removal detection was skipped"
                  data-testid={`sync-log-removal-detail-${log.id}`}
                  className="-m-[5px] inline-flex cursor-help items-center justify-center rounded-sm p-[5px] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={(e) => { e.stopPropagation(); }}
                >
                  <Info className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{note}</TooltipContent>
            </Tooltip>
          )}
        </span>
      </span>
    );
  }
  return <span className="text-muted-foreground">-</span>;
}

function LastRemovalCheck({ at }: { at: string | null }): JSX.Element {
  return (
    <p className="text-sm text-muted-foreground" data-testid="sync-logs-last-removal-check">
      {at
        ? `Removal check last ran ${formatDistanceToNow(new Date(at), { addSuffix: true })}`
        : 'Removal check has not run yet'}
    </p>
  );
}

function getSyncStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge variant="success">Completed</Badge>;
    case 'running':
      return <Badge variant="default">Running</Badge>;
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>;
    case 'pending':
      return <Badge variant="secondary">Pending</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt || !completedAt) return '-';
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationMs = end - start;

  if (durationMs < 1000) return `${String(durationMs)}ms`;
  if (durationMs < 60000) return `${String(Math.round(durationMs / 1000))}s`;
  return `${String(Math.round(durationMs / 60000))}m`;
}

export function SyncLogsList({ logs, lastRemovalCheckAt, isLoading }: SyncLogsListProps): JSX.Element {
  const [selectedLog, setSelectedLog] = useState<SyncLog | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const handleRowClick = (log: SyncLog): void => {
    // Only open detail dialog for completed syncs (they have detail records)
    if (log.status === 'completed' || log.status === 'failed') {
      setSelectedLog(log);
      setDetailDialogOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sync History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (logs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sync History</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No sync history available.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Sync History</CardTitle>
          <LastRemovalCheck at={lastRemovalCheckAt} />
        </CardHeader>
        <CardContent>
          <TooltipProvider>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Processed</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>
                    Removals
                    <span className="sr-only">: removal check and listings removed</span>
                  </TableHead>
                  <TableHead>Errors</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow
                    key={log.id}
                    data-testid={`sync-log-row-${log.id}`}
                    className={
                      log.status === 'completed' || log.status === 'failed'
                        ? 'cursor-pointer hover:bg-muted/50'
                        : ''
                    }
                    onClick={() => { handleRowClick(log); }}
                  >
                    <TableCell>
                      <Badge variant="outline">
                        {log.syncType === 'initial' ? 'Initial' : 'Incremental'}
                      </Badge>
                    </TableCell>
                    <TableCell>{getSyncStatusBadge(log.status)}</TableCell>
                    <TableCell>
                      {log.startedAt
                        ? formatDistanceToNow(new Date(log.startedAt), { addSuffix: true })
                        : '-'}
                    </TableCell>
                    <TableCell>{formatDuration(log.startedAt, log.completedAt)}</TableCell>
                    <TableCell>{log.locationsProcessed}</TableCell>
                    <TableCell>{log.locationsCreated ?? '-'}</TableCell>
                    <TableCell>{log.locationsUpdated ?? '-'}</TableCell>
                    <TableCell data-testid={`sync-log-removal-${log.id}`}>
                      <RemovalCheckCell log={log} />
                    </TableCell>
                    <TableCell>
                      {log.errorCount > 0 ? (
                        <Dialog>
                          <DialogTrigger asChild>
                            <button
                              className="cursor-pointer"
                              data-testid={`sync-log-error-${log.id}`}
                              onClick={(e) => { e.stopPropagation(); }}
                            >
                              <Badge variant="destructive">{log.errorCount}</Badge>
                            </button>
                          </DialogTrigger>
                          <DialogContent className="max-w-md">
                            <DialogHeader>
                              <DialogTitle>Error Details</DialogTitle>
                            </DialogHeader>
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                              {log.errorDetails ?? 'No details available'}
                            </p>
                          </DialogContent>
                        </Dialog>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TooltipProvider>
        </CardContent>
      </Card>

      {/* Sync Log Detail Dialog */}
      <SyncLogDetailDialog
        syncLog={selectedLog}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
      />
    </>
  );
}
